import { Spin } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { IMAGE_COMING_SOON } from '~/core/constants';
import { DesignSection, ProductType } from '~/core/enums';
import { isCustomWorkProduct, isWarrantyProduct } from '~/core/helpers';
import { useGetDesignSectionTranslations } from '~/core/hooks/useGetDesignSectionTranslations';
import { OptionalProduct } from '~/core/models';
import { getProductDescription, imgUrl } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  type CustomLaborData,
  addOneOptionalItem,
  clearSelectedWarrantyProduct,
  deselectOptionalItem,
  removeOneOptionalItem,
  resetCustomLaborOptionalItemsForProduct,
  selectPickedCustomLaborOptionalItems,
  selectSelectedOptionalItems,
  selectSelectedWarrantyProductId,
  selectWarrantyProduct,
  setCustomLaborOptionalItemsForProduct,
  setOptionalItemQuantity,
} from '~/redux/slices/optionalItems';
import DesignStepContainer from '~molecules/DesignStepContainer';
import ApplianceCard, {
  type ApplianceCardProps,
} from '~organisms/ApplianceCard';
import CustomLaborModal from '~organisms/CustomLaborModal/';
import DesignSectionHeader from '~organisms/DesignSectionHeader';
import * as St from '~organisms/SidePanelAddOnOptionsTab/SidePanelAddOnOptionsTab.styles';
import WarrantyDetailsModal from '~organisms/WarrantyDetailsModal';

const SidePanelAddOnOptionsTab = () => {
  const dispatch = useAppDispatch();
  const { visibleOptionalItems: optionalItems, isFetchingOptionalItems } =
    useOptionalItems();
  const selectedOptionalItems = useAppSelector(selectSelectedOptionalItems);
  const selectedWarrantyBy = useAppSelector(selectSelectedWarrantyProductId);
  const selectedCustomLaborItems = useAppSelector(
    selectPickedCustomLaborOptionalItems
  );
  const [warrantyCandidate, setWarrantyCandidate] = useState<
    OptionalProduct | undefined
  >();
  const [selectedCustomLabor, setSelectedCustomLabor] = useState<
    OptionalProduct | undefined
  >();
  const [warrantyProducts, customWorkProducts, optionalProducts] = useMemo(
    () =>
      optionalItems.reduce<
        [OptionalProduct[], OptionalProduct[], OptionalProduct[]]
      >(
        ([warranties, customWorks, others], item) =>
          isWarrantyProduct(item)
            ? [[...warranties, item], customWorks, others]
            : isCustomWorkProduct(item)
              ? [warranties, [...customWorks, item], others]
              : [warranties, customWorks, [...others, item]],
        [[], [], []]
      ),
    [optionalItems]
  );
  const selectedWarranty = useMemo(
    () =>
      warrantyProducts.find(
        (item) =>
          item.qteSubCode &&
          selectedWarrantyBy[item.qteSubCode] === item.productId
      ),
    [selectedWarrantyBy, warrantyProducts]
  );
  const isChangingSelectedWarranty = Boolean(
    warrantyCandidate?.qteSubCode &&
    selectedWarrantyBy[warrantyCandidate.qteSubCode] &&
    selectedWarrantyBy[warrantyCandidate.qteSubCode] !==
      warrantyCandidate.productId
  );

  const selectWarrantyCandidate = useCallback(() => {
    if (warrantyCandidate && warrantyCandidate.qteSubCode) {
      dispatch(
        selectWarrantyProduct([
          warrantyCandidate.qteSubCode,
          warrantyCandidate.productId,
        ])
      );
      setWarrantyCandidate(undefined);
    }
  }, [warrantyCandidate, dispatch, setWarrantyCandidate]);
  const addCustomLabor = useCallback(
    (customLaborOptionalItems: CustomLaborData[]) => {
      const productId = selectedCustomLabor?.productId;

      if (productId) {
        if (customLaborOptionalItems.length > 0) {
          dispatch(
            setCustomLaborOptionalItemsForProduct({
              productId,
              customLaborOptionalItems,
            })
          );
        } else {
          dispatch(resetCustomLaborOptionalItemsForProduct(productId));
        }
      }

      setSelectedCustomLabor(undefined);
    },
    [dispatch, selectedCustomLabor?.productId]
  );
  const makeApplianceCardProps = (
    item: OptionalProduct
  ): ApplianceCardProps => ({
    image: imgUrl(item.imageUrl ?? IMAGE_COMING_SOON, 300),
    title: getProductDescription(item),
    showPriceIfZero: false,
    priceWithoutDiscount: item.price,
    unitOfMeasure: item.unitOfMeasure?.toLowerCase(),
  });

  const getCustomLaborItemsQuantity = (customLaborItems: CustomLaborData[]) =>
    customLaborItems.reduce(
      (quantity, customLaborItem) => quantity + customLaborItem.quantity,
      0
    ) || undefined;

  const { title, description } = useGetDesignSectionTranslations(
    DesignSection.OPTIONAL_PRODUCTS
  );

  return (
    <>
      <DesignSectionHeader title={title} description={description} />

      {isFetchingOptionalItems ? (
        <Spin style={{ fontSize: 24, marginLeft: '40%' }} />
      ) : (
        <DesignStepContainer>
          {warrantyProducts.map((item) => {
            const isSelected =
              item.qteSubCode &&
              (selectedWarrantyBy[item.qteSubCode] === item.productId ||
                (warrantyCandidate?.qteSubCode === item.qteSubCode &&
                  warrantyCandidate.productId === item.productId));

            return (
              <ApplianceCard
                {...makeApplianceCardProps(item)}
                key={item.productId}
                isSelected={Boolean(isSelected)}
                onClick={() => {
                  if (!item.qteSubCode) {
                    return;
                  }
                  const qteSubCode = item.qteSubCode;
                  const currentSelectedId = selectedWarrantyBy[qteSubCode];

                  if (currentSelectedId === item.productId) {
                    dispatch(clearSelectedWarrantyProduct(qteSubCode));

                    return;
                  }

                  setWarrantyCandidate(item);
                }}
                applianceType={ProductType.MODIFY_ON}
              />
            );
          })}

          {customWorkProducts.map((item) => {
            const quantity = getCustomLaborItemsQuantity(
              selectedCustomLaborItems[item.productId] || []
            );

            const openCustomLabor = () => setSelectedCustomLabor(item);

            return (
              <ApplianceCard
                {...makeApplianceCardProps(item)}
                key={item.productId}
                isSelected={
                  selectedCustomLaborItems[item.productId]?.length > 0
                }
                onClick={openCustomLabor}
                applianceType={ProductType.OPTIONAL_PRODUCT}
                counterProps={{
                  quantity,
                  // Pass undefined to onIncrease hide the +Add button when quantity > 0.
                  onIncrease: (quantity ?? 0) > 0 ? undefined : openCustomLabor,
                }}
              />
            );
          })}

          {optionalProducts.map((item) => {
            const itemQty = selectedOptionalItems[item.productId] ?? 0;
            const isSelected = itemQty > 0;

            return (
              <ApplianceCard
                {...makeApplianceCardProps(item)}
                key={item.productId}
                isSelected={isSelected}
                onClick={() => {
                  if (isSelected) {
                    dispatch(deselectOptionalItem(item.productId));
                  } else {
                    dispatch(addOneOptionalItem(item.productId));
                  }
                }}
                counterProps={{
                  quantity: isSelected ? itemQty : undefined,
                  onIncrease: () =>
                    dispatch(addOneOptionalItem(item.productId)),
                  onDecrease: () =>
                    dispatch(removeOneOptionalItem(item.productId)),
                  onUpdateValue: (quantity) =>
                    dispatch(
                      setOptionalItemQuantity({
                        productId: item.productId,
                        quantity,
                      })
                    ),
                }}
                applianceType={ProductType.OPTIONAL_PRODUCT}
              />
            );
          })}
        </DesignStepContainer>
      )}

      <CustomLaborModal
        customLabor={selectedCustomLabor}
        onConfirm={addCustomLabor}
        onCancel={() => setSelectedCustomLabor(undefined)}
      />

      <WarrantyDetailsModal
        onClose={() => setWarrantyCandidate(undefined)}
        onConfirm={selectWarrantyCandidate}
        warrantyCandidate={
          isChangingSelectedWarranty ? undefined : warrantyCandidate
        }
      />

      <St.WarrantyWarningModal
        isVisible={isChangingSelectedWarranty}
        onCancel={() => setWarrantyCandidate(undefined)}
        onConfirm={() => {
          if (warrantyCandidate?.qteSubCode) {
            dispatch(
              clearSelectedWarrantyProduct(warrantyCandidate.qteSubCode)
            );
          }
          selectWarrantyCandidate();
        }}
        title={`Change to ${warrantyCandidate?.name}`}
        description={`By selecting ${warrantyCandidate?.name}, you will be removing ${selectedWarranty?.name}. Do you want to make the change?`}
        buttons={{ cancel: 'Cancel', confirm: 'Change' }}
      />
    </>
  );
};

export default SidePanelAddOnOptionsTab;
